import type { EffectiveModelAtlas } from '@ayumia/contracts';
import { curve, type Side } from '../../graph/graph-model';

/**
 * Mise en place de l'atlas du modèle (ADR-130 tranche 1) : une colonne par couche qui apporte des objets
 * (Core, chaque Pack, client), repliée en sous-colonnes au-delà de `ROWS` objets ; les liens ne sont tracés
 * que pour l'objet choisi (ses relations déclarées et celles qui le visent), le reste est atténué.
 */

export type AtlasObject = EffectiveModelAtlas['objects'][number];
export type AtlasRelation = AtlasObject['relations'][number];

export const ATLAS_CARD_W = 196;
export const ATLAS_CARD_H = 64;
const ROWS = 10;
const ROW_GAP = 12;
const SUB_GAP = 24;
const LAYER_GAP = 72;
const PAD = 20;
const TOP = 52;

export interface AtlasCard {
  key: string;
  object: AtlasObject;
  lineage: string;
  x: number;
  y: number;
  sides: Set<Side>;
}

export interface AtlasLink {
  key: string;
  relation: AtlasRelation;
  from: string;
  to: string;
  d: string;
  labelX: number;
  labelY: number;
}

export interface AtlasZone {
  lineage: string;
  x: number;
  w: number;
  count: number;
}

export interface AtlasLayout {
  width: number;
  height: number;
  cards: AtlasCard[];
  links: AtlasLink[];
  zones: AtlasZone[];
}

export const CARDINALITY: Readonly<Record<string, string>> = { one_to_one: '1—1', one_to_many: '1—n', many_to_one: 'n—1', many_to_many: 'n—n' };

export function lineageOfObject(object: AtlasObject): string {
  return object.lineage ?? object.origin_chain[0]?.lineage ?? 'core';
}

export function layoutAtlas(atlas: EffectiveModelAtlas, options: { hiddenLineages: ReadonlySet<string>; selected?: string }): AtlasLayout {
  const order = atlas.layers.map((layer) => layer.lineage);
  const byLineage = new Map<string, AtlasObject[]>();
  for (const object of atlas.objects) {
    const lineage = lineageOfObject(object);
    if (options.hiddenLineages.has(lineage)) continue;
    byLineage.set(lineage, [...(byLineage.get(lineage) ?? []), object]);
  }
  const lineages = [...byLineage.keys()].sort((a, b) => (order.indexOf(a) === -1 ? 99 : order.indexOf(a)) - (order.indexOf(b) === -1 ? 99 : order.indexOf(b)) || a.localeCompare(b));
  const cards: AtlasCard[] = [];
  const zones: AtlasZone[] = [];
  let x = PAD;
  let tallest = 0;
  for (const lineage of lineages) {
    const objects = byLineage.get(lineage)!.sort((a, b) => a.object_type.localeCompare(b.object_type));
    const subColumns = Math.ceil(objects.length / ROWS);
    const zoneW = subColumns * ATLAS_CARD_W + (subColumns - 1) * SUB_GAP + 2 * 12;
    zones.push({ lineage, x, w: zoneW, count: objects.length });
    objects.forEach((object, index) => {
      const column = Math.floor(index / ROWS);
      const row = index % ROWS;
      cards.push({ key: object.object_type, object, lineage, x: x + 12 + column * (ATLAS_CARD_W + SUB_GAP), y: TOP + row * (ATLAS_CARD_H + ROW_GAP), sides: new Set() });
    });
    tallest = Math.max(tallest, Math.min(objects.length, ROWS) * (ATLAS_CARD_H + ROW_GAP));
    x += zoneW + LAYER_GAP;
  }
  const width = Math.max(640, x - LAYER_GAP + PAD);
  const height = TOP + tallest + PAD * 2;

  const links: AtlasLink[] = [];
  const cardOf = new Map(cards.map((card) => [card.key, card]));
  const selected = options.selected === undefined ? undefined : cardOf.get(options.selected);
  if (selected !== undefined) {
    const relations: AtlasRelation[] = [];
    for (const object of atlas.objects) for (const relation of object.relations) if (relation.source_type === selected.key || relation.target_type === selected.key) relations.push(relation);
    const taken = new Map<string, number>();
    for (const relation of relations) {
      const from = cardOf.get(relation.source_type);
      const to = cardOf.get(relation.target_type);
      if (from === undefined || to === undefined || from === to) continue;
      let d: string;
      let labelX: number;
      let labelY: number;
      if (Math.abs(from.x - to.x) > ATLAS_CARD_W / 2) {
        const [left, right] = from.x < to.x ? [from, to] : [to, from];
        left.sides.add('right');
        right.sides.add('left');
        const start = { x: left.x + ATLAS_CARD_W, y: left.y + ATLAS_CARD_H / 2 };
        const end = { x: right.x, y: right.y + ATLAS_CARD_H / 2 };
        d = from === left ? curve(start, end, false) : curve(end, start, false);
        labelX = (start.x + end.x) / 2;
        labelY = (start.y + end.y) / 2;
      } else {
        const [upper, lower] = from.y < to.y ? [from, to] : [to, from];
        upper.sides.add('bottom');
        lower.sides.add('top');
        const start = { x: upper.x + ATLAS_CARD_W / 2, y: upper.y + ATLAS_CARD_H };
        const end = { x: lower.x + ATLAS_CARD_W / 2, y: lower.y };
        d = from === upper ? curve(start, end, true) : curve(end, start, true);
        labelX = start.x;
        labelY = (start.y + end.y) / 2;
      }
      const slot = `${Math.round(labelX / 10)}:${Math.round(labelY / 10)}`;
      const shift = taken.get(slot) ?? 0;
      taken.set(slot, shift + 1);
      links.push({ key: `${relation.source_type}.${relation.relation_id}`, relation, from: from.key, to: to.key, d, labelX, labelY: labelY + shift * 22 });
    }
  }
  return { width, height, cards, links, zones };
}
